import { Map } from 'immutable'
import _isFinite from 'lodash/isFinite'
import _parseInt from 'lodash/parseInt'
import { get as getCookie } from 'cookies-js'
import { parse as parseQuery } from '../query-string/index'
import { formatCheckoutOrderMarketingValue } from './format-checkout-order-marketing-value'

/**
 * Parse GA4 client id from `_ga` cookie (`GA1.1.x.y` → `x.y`).
 * @param {string|undefined} gaCookie
 * @returns {string|undefined}
 */
export function parseGaClientIdFromCookie (gaCookie) {
  if (!gaCookie || typeof gaCookie !== 'string') {
    return undefined
  }
  const parts = gaCookie.split('.')
  if (parts.length < 4) {
    return undefined
  }
  const clientId = `${parts[2]}.${parts[3]}`
  return clientId.trim() !== '' ? clientId : undefined
}

/**
 * Parse GA4 session id and number from `_ga_<MEASUREMENT>` cookie.
 * Supports GS1 (`GS1.1.<session>.<number>...`) and GS2 (`GS2.1.s<session>$o<number>...`).
 * @param {string|undefined} gaSessionCookie
 * @returns {{ gaSessionId?: string, gaSessionNumber?: number }}
 */
export function parseGaSessionFromCookie (gaSessionCookie) {
  if (!gaSessionCookie || typeof gaSessionCookie !== 'string') {
    return {}
  }

  if (gaSessionCookie.startsWith('GS2.')) {
    const sessionIdMatch = gaSessionCookie.match(/\.s(\d+)/)
    const sessionNumMatch = gaSessionCookie.match(/\$o(\d+)/)
    const gaSessionId = sessionIdMatch?.[1]
    const gaSessionNumber = sessionNumMatch?.[1] != null
      ? _parseInt(sessionNumMatch[1], 10)
      : undefined
    return {
      ...(gaSessionId && gaSessionId.trim() !== '' ? { gaSessionId } : {}),
      ...(_isFinite(gaSessionNumber) ? { gaSessionNumber } : {}),
    }
  }

  const parts = gaSessionCookie.split('.')
  if (parts.length < 4) {
    return {}
  }
  const gaSessionId = parts[2]
  const gaSessionNumber = _parseInt(parts[3], 10)
  return {
    ...(gaSessionId && gaSessionId.trim() !== '' ? { gaSessionId } : {}),
    ...(_isFinite(gaSessionNumber) ? { gaSessionNumber } : {}),
  }
}

/**
 * Find the first `_ga_*` session cookie (excluding `_ga` itself).
 * @returns {{ gaSessionId?: string, gaSessionNumber?: number }}
 */
export function getGaSessionFromDocumentCookies () {
  if (typeof document === 'undefined' || !document.cookie) {
    return {}
  }
  const cookies = document.cookie.split(';')
  for (let i = 0; i < cookies.length; i += 1) {
    const segment = cookies[i].trim()
    const eq = segment.indexOf('=')
    if (eq > 0) {
      const name = segment.slice(0, eq)
      if (name !== '_ga' && name.startsWith('_ga_')) {
        const value = decodeURIComponent(segment.slice(eq + 1))
        const parsed = parseGaSessionFromCookie(value)
        if (parsed.gaSessionId) {
          return parsed
        }
      }
    }
  }
  return {}
}

/**
 * Extract click id from Google Ads `_gcl_aw` or `_gcl_gb` cookie when URL lacks gclid.
 * @returns {{ gclid?: string, wbraid?: string, gbraid?: string }}
 */
export function getGoogleClickIdFromCookies () {
  const gclAw = getCookie('_gcl_aw')
  if (gclAw && typeof gclAw === 'string') {
    const parts = gclAw.split('.')
    const clickId = parts.length >= 3 ? parts[parts.length - 1] : gclAw
    if (clickId && clickId.trim() !== '') {
      return { gclid: clickId }
    }
  }
  const gclGb = getCookie('_gcl_gb')
  if (gclGb && typeof gclGb === 'string' && gclGb.trim() !== '') {
    return { gbraid: gclGb }
  }
  return {}
}

/**
 * Read TikTok `_ttp` cookie when present.
 * @returns {string|undefined}
 */
export function getTtpFromCookie () {
  const ttp = getCookie('_ttp')
  if (ttp && typeof ttp === 'string' && ttp.trim() !== '') {
    return ttp.trim()
  }
  return undefined
}

/**
 * Build Facebook click id cookie from fbclid when `_fbc` is absent.
 * @param {string|undefined} fbclid
 * @returns {string|undefined}
 */
export function buildFbcFromFbclid (fbclid) {
  if (!fbclid || typeof fbclid !== 'string' || fbclid.trim() === '') {
    return undefined
  }
  return `fb.1.${Date.now()}.${fbclid.trim()}`
}

/**
 * Read inbound tracking click ids from Redux inboundTracking map.
 * @param {import('immutable').Map|undefined} inboundTracking
 * @returns {{
 *   gclid?: string,
 *   wbraid?: string,
 *   gbraid?: string,
 *   fbclid?: string,
 *   msclkid?: string,
 *   ttclid?: string,
 * }}
 */
export function getInboundClickIds (inboundTracking) {
  if (!Map.isMap(inboundTracking) || inboundTracking.size === 0) {
    return {}
  }
  const data = inboundTracking.get('data', Map())
  const utmString = data.getIn(['strings', 'utm'])
  const utmObject = utmString ? parseQuery(utmString) : {}
  const pick = (key) => {
    const fromData = data.get(key)
    const fromUtm = utmObject[key]
    const value = fromData || fromUtm
    return value != null && String(value).trim() !== '' ? String(value).trim() : undefined
  }
  const gclid = pick('gclid')
  const wbraid = pick('wbraid')
  const gbraid = pick('gbraid')
  const fbclid = pick('fbclid')
  const msclkid = pick('msclkid') || pick('MicrosoftClickId')
  const ttclid = pick('ttclid')
  return {
    ...(gclid ? { gclid } : {}),
    ...(wbraid ? { wbraid } : {}),
    ...(gbraid ? { gbraid } : {}),
    ...(fbclid ? { fbclid } : {}),
    ...(msclkid ? { msclkid } : {}),
    ...(ttclid ? { ttclid } : {}),
  }
}

/**
 * NR-safe checkout tracking booleans (no raw click ids or dollar amounts).
 * @param {Object} options
 * @param {import('immutable').Map} [options.inboundTracking]
 * @param {import('immutable').Map} [options.plans]
 * @returns {{
 *   hasInboundUtm: boolean,
 *   hasGclid: boolean,
 *   hasFbclid: boolean,
 *   hasMsclkid: boolean,
 *   hasTtclid: boolean,
 *   hasTtp: boolean,
 *   hasMarketingValue: boolean,
 *   planSku: string,
 * }}
 */
export function getCheckoutTrackingNewRelicBooleans ({ inboundTracking, plans }) {
  const clickIds = getInboundClickIds(inboundTracking)
  const cookieClickIds = getGoogleClickIdFromCookies()
  const data = Map.isMap(inboundTracking) ? inboundTracking.get('data', Map()) : Map()
  const hasInboundUtm = data.hasIn(['strings', 'utm']) || data.has('utm_source')
  const selectedPlan = Map.isMap(plans) ? (plans.get('selection', Map()) || Map()) : Map()
  const planSku = selectedPlan.get('sku', '') || ''
  const marketingValue = formatCheckoutOrderMarketingValue(selectedPlan)
  return {
    hasInboundUtm,
    hasGclid: Boolean(
      clickIds.gclid
      || clickIds.wbraid
      || clickIds.gbraid
      || cookieClickIds.gclid
      || cookieClickIds.wbraid
      || cookieClickIds.gbraid,
    ),
    hasFbclid: Boolean(clickIds.fbclid),
    hasMsclkid: Boolean(clickIds.msclkid),
    hasTtclid: Boolean(clickIds.ttclid),
    hasTtp: Boolean(getTtpFromCookie()),
    hasMarketingValue: _isFinite(marketingValue) && marketingValue > 0,
    planSku,
  }
}

/**
 * NR PageAction attributes for checkout tracking stamp (no raw ids or amounts).
 * @param {Object} options
 * @param {import('immutable').Map} [options.inboundTracking]
 * @param {import('immutable').Map} [options.plans]
 * @param {Object} [options.marketingTrackingData]
 * @returns {Object}
 */
export function buildCheckoutTrackingStampPageActionAttributes ({
  inboundTracking,
  plans,
  marketingTrackingData = {},
}) {
  return {
    ...getCheckoutTrackingNewRelicBooleans({ inboundTracking, plans }),
    hasGaClientId: Boolean(marketingTrackingData.ga_client_id),
    hasGaSessionId: Boolean(marketingTrackingData.ga_session_id),
    hasGaSessionNumber: marketingTrackingData.ga_session_number != null,
  }
}
